// ============================================================================
// FSA NOTES TAB  (FSA-2.2)
// ============================================================================
// Left-rail Notes screen. Lists every schedule under the engagement's
// NOTES_BUNDLE report with live coverage + saved totals. Row click opens
// the per-Note modal for class-level data entry + save.
//
// FSA-2.2 — adds the note-number + include/exclude controls that the
// consolidated FS PDF reads. Inline `#` editor + row-action menu (Mark
// Excluded / Mark Included / Edit Number) + toolbar "Auto-number Notes".
// Excluded rows are greyed/italic and skipped by the consolidated PDF.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn, MinimizedModalStrip, ActionMenu, type ActionMenuItem } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import { useFsaNotesList } from 'hooks/api/fsa/useFsaNotes';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import FsaNoteModal from 'components/modals/modules/fsa/FsaNoteModal';
// FSA-2.4 — LedgerDriftChip retired from this file; per-row TB drift
// now renders as a muted amber dot (matches Note Modal + Statements
// pattern). Aggregate count surfaces in the status filter chip strip.
import notificationService from 'services/notificationService';
import { fsaNotesService } from 'api/services/fsa/fsaNotesService';
import type { ID } from 'api/types';
import type { NoteListItem } from 'api/types/fsa/noteData.types';

interface Props {
  engagementId: ID;
  trialBalanceId: ID | null;   // for context — modal hydrates from backend regardless
  tabId?: string;
  moduleName?: string;
}

// FSA-2.4 — Status filter chip set. Module-scope so the matcher helper
// + the chip strip + the state can all reference the same union.
type StatusFilter = 'all' | 'draft' | 'submitted' | 'approved' | 'excluded' | 'drift';

const FsaNotesTab: React.FC<Props> = ({ engagementId, tabId, moduleName }) => {
  const { data: rows, loading, refetch } = useFsaNotesList(engagementId);
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const noteModalMinimizeRef = useRef<(() => void) | null>(null);

  const [openSchedule, setOpenSchedule] = useState<NoteListItem | null>(null);

  // FSA-2.4 — Display-mode toggle (Actual / Rounded). Persists in
  // localStorage so the auditor's preference carries across sessions.
  // Mirrors the pill toggle pattern used on Statement screens + Note
  // Modal header for consistency across the FSA module. Applies to
  // every amount column on the grid (CY/PY Closing).
  const DISPLAY_MODE_KEY = 'fsa.notes-tab.display-mode';
  const [displayMode, setDisplayModeState] = useState<'actual' | 'rounded'>(() => {
    if (typeof window === 'undefined') return 'actual';
    try {
      const v = window.localStorage.getItem(DISPLAY_MODE_KEY);
      return v === 'rounded' ? 'rounded' : 'actual';
    } catch { return 'actual'; }
  });
  const setDisplayMode = useCallback((mode: 'actual' | 'rounded') => {
    setDisplayModeState(mode);
    try { window.localStorage.setItem(DISPLAY_MODE_KEY, mode); } catch { /* noop */ }
  }, []);

  // FSA-2.4 — Status filter chip bar (single row above the grid).
  // 'all' is the default; other values filter by workflow stage or
  // applicability. 'drift' filters to notes flagged stale vs live TB.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Client-side search across schedule name + code + number. Filters the
  // displayed list — does not refetch (the API returns the full list).
  const [searchQuery, setSearchQuery] = useState<string>('');

  // TB drift detection — saved closing != live TB total. IsStale is
  // backend-computed (saved vs live recomputation) so we surface it via a
  // dedicated chip on the row. Excluded notes don't show the chip since
  // they're skipped from the consolidated FS PDF anyway.
  // FSA-2.4 — Drift detection uses the AUTHORITATIVE effective ledger
  // (TB + JE + cross-note in/out + inbound PL), NOT raw TB. Comparing
  // against raw `liveTbClosingTotal` produced false positives on
  // every note with cross-note routing / sign-sweep / PL→Reserves
  // bridging. The backend now ships `effectiveLedgerCy` on every row.
  //
  // The `isStale` SQL flag still uses the older incomplete formula
  // (saved + class-level cross-note vs raw TB) — kept for the "Stale"
  // status chip which signals "needs re-save" rather than "books
  // disagree". For the drift indicator (auditor-facing reconciliation
  // signal), we rely on the effective comparison.
  const hasTbDrift = useCallback((r: NoteListItem): boolean => {
    if (r.isExcluded) return false;
    if (!r.hasSavedData) return false;
    return Math.abs(r.closingActualTotal - r.effectiveLedgerCy) > 0.005;
  }, []);

  // FSA-2.4 — Composed filter: status filter chip + search query.
  // Status chip applies first (workflow stage / applicability /
  // drift), then search narrows within the chip-filtered set. Counts
  // for each chip displayed inline derive from the SAME function (no
  // chip-respective filtering loop in render).
  const matchesStatusFilter = useCallback((r: NoteListItem, filter: StatusFilter): boolean => {
    switch (filter) {
      case 'all':       return true;
      case 'draft':     return !r.isExcluded && r.hasSavedData && r.statusCode === 'DRAFT';
      case 'submitted': return !r.isExcluded && r.statusCode === 'SUBMITTED';
      case 'approved':  return !r.isExcluded && r.statusCode === 'APPROVED';
      case 'excluded':  return r.isExcluded;
      case 'drift':     return hasTbDrift(r);
    }
  }, [hasTbDrift]);

  const statusFilterCounts = useMemo(() => ({
    all:       rows.length,
    draft:     rows.filter(r => matchesStatusFilter(r, 'draft')).length,
    submitted: rows.filter(r => matchesStatusFilter(r, 'submitted')).length,
    approved:  rows.filter(r => matchesStatusFilter(r, 'approved')).length,
    excluded:  rows.filter(r => matchesStatusFilter(r, 'excluded')).length,
    drift:     rows.filter(r => matchesStatusFilter(r, 'drift')).length,
  }), [rows, matchesStatusFilter]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (!matchesStatusFilter(r, statusFilter)) return false;
      if (!q) return true;
      const name   = (r.scheduleName ?? '').toLowerCase();
      const code   = (r.scheduleCode ?? '').toLowerCase();
      const number = r.noteNumber != null ? String(r.noteNumber) : '';
      return name.includes(q) || code.includes(q) || number.includes(q);
    });
  }, [rows, searchQuery, statusFilter, matchesStatusFilter]);

  // FSA-2.2 — inline number editor + exclude/include + auto-number state.
  const [editingNumberFor, setEditingNumberFor] = useState<ID | null>(null);
  const [numberInput, setNumberInput] = useState<string>('');
  const [numberSavingFor, setNumberSavingFor] = useState<ID | null>(null);
  const [numberErrorFor, setNumberErrorFor] = useState<{ scheduleId: ID; message: string } | null>(null);

  const [pendingExclude, setPendingExclude] = useState<NoteListItem | null>(null);
  const [confirmClearFor, setConfirmClearFor] = useState<NoteListItem | null>(null);
  const [processingExclusionFor, setProcessingExclusionFor] = useState<ID | null>(null);

  const [autoNumberConfirmOpen, setAutoNumberConfirmOpen] = useState(false);
  const [autoNumbering, setAutoNumbering] = useState(false);

  // FSA-2.4 — Display-mode toggle moved earlier (above filteredRows
  // so the column header binding can read the latest value without
  // a use-before-declaration error).

  // ── Modal open / restore ──────────────────────────────────────────────
  const handleOpen = useCallback((row: NoteListItem) => {
    if (tabId) {
      const modalId = `${tabId}-fsa-note-${row.scheduleId}`;
      if (isMinimized(modalId)) {
        const m = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        m?.onRestore?.();
        return;
      }
    }
    if (openSchedule && openSchedule.scheduleId !== row.scheduleId && noteModalMinimizeRef.current) {
      noteModalMinimizeRef.current();
    }
    setOpenSchedule(row);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, openSchedule]);

  const handleRestoreModal = useCallback((scheduleId: ID) => {
    const row = rows.find(r => r.scheduleId === scheduleId);
    if (row) setOpenSchedule(row);
  }, [rows]);

  // ── Inline note-number editor ─────────────────────────────────────────
  const startEditNumber = useCallback((row: NoteListItem) => {
    if (row.isExcluded) return;
    setEditingNumberFor(row.scheduleId);
    setNumberInput(row.noteNumber != null ? String(row.noteNumber) : '');
    setNumberErrorFor(null);
  }, []);

  const cancelEditNumber = useCallback(() => {
    setEditingNumberFor(null);
    setNumberInput('');
    setNumberErrorFor(null);
  }, []);

  // FSA-2.3 — pending move-to-position confirmation. Captures the target
  // schedule + the requested position; the confirm dialog explains the
  // reshuffle before the auditor commits. Clearing the number (blank
  // input on an already-numbered note) is treated as "exclude please" by
  // the original semantic but doesn't fit the move flow — we route an
  // empty input through SetNoteNumber(null) instead.
  const [pendingMove, setPendingMove] = useState<{ row: NoteListItem; targetPosition: number } | null>(null);

  const saveNumber = useCallback(async (scheduleId: ID, row?: NoteListItem) => {
    const trimmed = numberInput.trim();

    // Blank input — clear via the legacy setNoteNumber(null) path.
    if (trimmed === '') {
      setNumberSavingFor(scheduleId);
      try {
        await fsaNotesService.setNoteNumber(engagementId, scheduleId, { noteNumber: null });
        setEditingNumberFor(null);
        setNumberInput('');
        setNumberErrorFor(null);
        notificationService.toast.success('Note number cleared.');
        refetch();
      } catch (e: any) {
        notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to clear note number.');
      } finally {
        setNumberSavingFor(null);
      }
      return;
    }

    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setNumberErrorFor({ scheduleId, message: 'Enter a positive integer or leave blank.' });
      return;
    }
    const target = row ?? rows.find(r => r.scheduleId === scheduleId);
    if (!target) {
      setNumberErrorFor({ scheduleId, message: 'Note not found in the current list.' });
      return;
    }
    // Same position → no-op (avoid prompting the auditor for a no-change move).
    if (target.noteNumber === parsed) {
      setEditingNumberFor(null);
      setNumberInput('');
      setNumberErrorFor(null);
      return;
    }
    // Open the move-to-position confirm dialog. The actual API call happens
    // on confirm; cancel keeps the inline editor open with the typed value.
    setPendingMove({ row: target, targetPosition: parsed });
  }, [engagementId, numberInput, refetch, rows]);

  // FSA-2.3 — execute the move once confirmed.
  const confirmMove = useCallback(async () => {
    if (!pendingMove) return;
    const { row, targetPosition } = pendingMove;
    setNumberSavingFor(row.scheduleId);
    try {
      await fsaNotesService.moveToPosition(engagementId, row.scheduleId, targetPosition);
      setPendingMove(null);
      setEditingNumberFor(null);
      setNumberInput('');
      setNumberErrorFor(null);
      notificationService.toast.success(`"${row.scheduleName}" moved to position ${targetPosition}.`);
      refetch();
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message || 'Failed to move note.';
      notificationService.toast.error(message);
    } finally {
      setNumberSavingFor(null);
    }
  }, [engagementId, pendingMove, refetch]);

  // ── Exclude / Include ─────────────────────────────────────────────────
  const performExclude = useCallback(async (row: NoteListItem, clearSavedData: boolean) => {
    setProcessingExclusionFor(row.scheduleId);
    try {
      await fsaNotesService.exclude(engagementId, row.scheduleId, { clearSavedData });
      notificationService.toast.success(`"${row.scheduleName}" marked excluded.`);
      setPendingExclude(null);
      setConfirmClearFor(null);
      refetch();
    } catch (e: any) {
      const status = e?.response?.status;
      const errors: string[] = e?.response?.data?.errors ?? [];
      if (status === 409 && errors.includes('requires_clear_confirmation')) {
        // Drop the pre-confirm and open the clear-data confirm.
        setPendingExclude(null);
        setConfirmClearFor(row);
      } else {
        notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to exclude note.');
      }
    } finally {
      setProcessingExclusionFor(null);
    }
  }, [engagementId, refetch]);

  const handleMarkExcluded = useCallback((row: NoteListItem) => {
    setPendingExclude(row);
  }, []);

  const handleMarkIncluded = useCallback(async (row: NoteListItem) => {
    setProcessingExclusionFor(row.scheduleId);
    try {
      await fsaNotesService.include(engagementId, row.scheduleId);
      notificationService.toast.success(`"${row.scheduleName}" reinstated.`);
      refetch();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || e?.message || 'Failed to reinstate note.');
    } finally {
      setProcessingExclusionFor(null);
    }
  }, [engagementId, refetch]);

  // ── Auto-number ───────────────────────────────────────────────────────
  const handleAutoNumber = useCallback(async () => {
    setAutoNumberConfirmOpen(false);
    setAutoNumbering(true);
    try {
      const response = await fsaNotesService.autoNumber(engagementId);
      const count = response.data?.assignedCount ?? 0;
      notificationService.toast.success(`${count} note${count === 1 ? '' : 's'} renumbered.`);
      refetch();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message || e?.message || 'Auto-numbering failed.');
    } finally {
      setAutoNumbering(false);
    }
  }, [engagementId, refetch]);

  // ── Reset transient state when rows refresh ───────────────────────────
  useEffect(() => {
    // If a number we were editing got refreshed away (e.g. via auto-number),
    // close the editor.
    if (editingNumberFor && !rows.some(r => r.scheduleId === editingNumberFor)) {
      cancelEditNumber();
    }
  }, [rows, editingNumberFor, cancelEditNumber]);

  // ── Columns ───────────────────────────────────────────────────────────
  const columns: SyutGridColumn<any>[] = useMemo(() => [
    {
      id: 'noteNumber',
      header: '#',
      size: 86,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        if (r.isExcluded) {
          return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
        }
        if (editingNumberFor === r.scheduleId) {
          const err = numberErrorFor?.scheduleId === r.scheduleId ? numberErrorFor.message : null;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="text"
                  className="form-input"
                  autoFocus
                  value={numberInput}
                  onChange={(e) => { setNumberInput(e.target.value); setNumberErrorFor(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveNumber(r.scheduleId, r); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEditNumber(); }
                  }}
                  disabled={numberSavingFor === r.scheduleId}
                  placeholder="—"
                  style={{ width: 50, padding: '2px 4px', fontSize: '0.8rem', textAlign: 'right' }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => saveNumber(r.scheduleId, r)}
                  disabled={numberSavingFor === r.scheduleId}
                  style={{ padding: '2px 6px', minWidth: 0 }}
                  title="Save"
                >
                  <IconSystem.check size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={cancelEditNumber}
                  disabled={numberSavingFor === r.scheduleId}
                  style={{ padding: '2px 6px', minWidth: 0 }}
                  title="Cancel"
                >
                  <IconSystem.x size={12} />
                </button>
              </div>
              {err && <div style={{ fontSize: '0.65rem', color: '#dc2626' }}>{err}</div>}
            </div>
          );
        }
        return (
          <button
            type="button"
            onClick={() => startEditNumber(r)}
            title="Click to edit"
            style={{
              background: 'transparent', border: '1px dashed transparent',
              padding: '1px 6px', borderRadius: 4, cursor: 'pointer',
              minWidth: 36, textAlign: 'right',
              fontWeight: r.noteNumber != null ? 600 : 400,
              color: r.noteNumber != null ? 'var(--text-primary)' : 'var(--text-secondary)',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--form-border-color)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'transparent'; }}
          >
            {r.noteNumber != null ? r.noteNumber : '—'}
          </button>
        );
      },
    },
    {
      id: 'scheduleName',
      header: 'Note',
      accessorKey: 'scheduleName',
      size: 280,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const muted = r.isExcluded ? { color: 'var(--text-secondary)', fontStyle: 'italic' as const } : undefined;
        const drift = hasTbDrift(r);
        // FSA-2.4 — drift amount = |note total − effective ledger|
        // (same formula as hasTbDrift) so the dot's tooltip number
        // matches the criterion that triggered the dot.
        const driftAmount = drift
          ? Math.abs((r.closingActualTotal ?? 0) - (r.effectiveLedgerCy ?? 0))
          : 0;
        return (
          <div style={muted}>
            <div style={{ fontWeight: r.isExcluded ? 500 : 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{r.scheduleName}</span>
              {/* FSA-2.4 — Drift dot. Compares saved Note Total
                  against EFFECTIVE LEDGER (TB + JE + cross-note +
                  PL inflows), not raw TB. Aggregate count surfaces
                  in the status filter chip strip above the grid. */}
              {drift && (
                <span
                  style={{
                    color: '#d97706',
                    fontSize: '0.85rem',
                    cursor: 'help',
                    lineHeight: 1,
                  }}
                  title={`Drift ₹${driftAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — saved Note total differs from the effective ledger (after JE + cross-note + PL adjustments). Open the Note to investigate.`}
                  aria-label={`Effective ledger drift ${driftAmount}`}
                >●</span>
              )}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{r.scheduleCode}</div>
          </div>
        );
      },
    },
    // FSA-2.4 — Amount columns (mode-aware). The header pill toggle
    // above the grid picks actual vs rounded; the column header label
    // updates accordingly. CY/PY shown side-by-side per the auditor's
    // reconciliation request — quick scan vs prior year.
    {
      id: 'cyClosing',
      header: `CY Closing${displayMode === 'rounded' ? ' (Rounded)' : ''}`,
      size: 150,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const sign = r.isCreditBalance ? -1 : 1;
        const value = displayMode === 'rounded' ? r.closingRoundedTotal : r.closingActualTotal;
        return (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            display: 'block',
            textAlign: 'right',
            fontWeight: r.isExcluded ? 400 : 600,
            color: r.isExcluded ? 'var(--text-secondary)' : undefined,
          }}>
            {(value * sign).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      id: 'pyClosing',
      header: `PY Closing${displayMode === 'rounded' ? ' (Rounded)' : ''}`,
      size: 150,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const sign = r.isCreditBalance ? -1 : 1;
        const value = displayMode === 'rounded' ? r.openingRoundedTotal : r.openingActualTotal;
        return (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            display: 'block',
            textAlign: 'right',
            color: r.isExcluded ? 'var(--text-secondary)' : undefined,
          }}>
            {(value * sign).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    // FSA-2.4 — Renamed from "CY/PY Live TB" to "CY/PY Effective".
    // Backend ships effective ledger = TB + JE + cross-note in/out
    // + inbound PL. Comparing the saved Note Total against THIS figure
    // (not raw TB) gives a reconciliation signal that doesn't false-
    // positive on legitimate cross-note movements / sign-sweeps /
    // PL→Reserves bridging. Tooltip on the header column explains
    // the composition.
    {
      id: 'cyEffective',
      header: 'CY Effective',
      size: 160,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const sign = r.isCreditBalance ? -1 : 1;
        return (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              display: 'block',
              textAlign: 'right',
              color: 'var(--text-secondary)',
            }}
            title="Effective ledger = TB closing + JE + incoming cross-note + inbound PL − outgoing cross-note. Compare against CY Closing to read true drift."
          >
            {(r.effectiveLedgerCy * sign).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      id: 'pyEffective',
      header: 'PY Effective',
      size: 160,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const sign = r.isCreditBalance ? -1 : 1;
        return (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              display: 'block',
              textAlign: 'right',
              color: 'var(--text-secondary)',
            }}
            title="Effective ledger (PY) = TB opening + incoming cross-note + inbound PL − outgoing cross-note. JEs don't affect openings."
          >
            {(r.effectiveLedgerPy * sign).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      size: 110,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        if (r.isExcluded) return <span style={chip('#f1f5f9', '#475569')}>—</span>;
        if (!r.hasSavedData) return <span style={chip('#f1f5f9', '#475569')}>Not started</span>;
        if (r.isStale)       return <span style={chip('#fef3c7', '#92400e')} title="Re-save to refresh">Stale</span>;
        if (r.statusCode === 'APPROVED')  return <span style={chip('#dcfce7', '#166534')}>Approved</span>;
        if (r.statusCode === 'SUBMITTED') return <span style={chip('#dbeafe', '#1e40af')}>Submitted</span>;
        return <span style={chip('#e0f2fe', '#0369a1')}>Draft</span>;
      },
    },
    // FSA-2.4 — "Include" renamed to "Applicable" — matches the
    // existing CF Statement toolbar action "Mark Not Applicable" /
    // "Mark Applicable" for terminology consistency across the FSA
    // module. Same semantics: artifact is/isn't part of issued FS.
    {
      id: 'isExcluded',
      header: 'Applicable',
      size: 130,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        return r.isExcluded
          ? <span style={chip('#f1f5f9', '#475569')} title="Skipped from consolidated FS PDF">✗ Not Applicable</span>
          : <span style={chip('#dcfce7', '#166534')}>✓ Applicable</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      cell: ({ row }: any) => {
        const r: NoteListItem = row.original;
        const busy = processingExclusionFor === r.scheduleId || numberSavingFor === r.scheduleId;
        const items: ActionMenuItem[] = [];
        items.push({
          id: 'open',
          label: 'Open Note',
          icon: <IconSystem.edit size={14} />,
          onClick: () => handleOpen(r),
        });
        if (!r.isExcluded) {
          items.push({
            id: 'edit-number',
            label: r.noteNumber != null ? 'Edit number' : 'Set number',
            icon: <IconSystem.tag size={14} />,
            onClick: () => startEditNumber(r),
          });
        }
        if (r.isExcluded) {
          items.push({
            id: 'include',
            label: 'Mark Included',
            icon: <IconSystem.check size={14} />,
            onClick: () => handleMarkIncluded(r),
            disabled: busy,
          });
        } else {
          items.push({
            id: 'exclude',
            label: 'Mark Excluded',
            icon: <IconSystem.x size={14} />,
            onClick: () => handleMarkExcluded(r),
            disabled: busy,
            variant: 'danger',
          });
        }
        items.push({
          id: 'refresh',
          label: 'Refresh from TB',
          icon: <IconSystem.refresh size={14} />,
          onClick: () => refetch(),
        });
        return <ActionMenu items={items} />;
      },
    },
  ], [
    editingNumberFor, numberInput, numberErrorFor, numberSavingFor,
    processingExclusionFor, hasTbDrift,
    startEditNumber, cancelEditNumber, saveNumber,
    handleOpen, handleMarkExcluded, handleMarkIncluded, refetch,
    displayMode, // FSA-2.4 — column headers + amount cells re-render on toggle
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={toolbarStyle}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          {loading
            ? 'Loading…'
            : searchQuery.trim()
              ? `${filteredRows.length} of ${rows.length} note${rows.length === 1 ? '' : 's'}`
              : `${rows.length} note${rows.length === 1 ? '' : 's'}`}
        </div>
        {tabId && <MinimizedModalStrip tabId={tabId} />}
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <IconSystem.search size={12} style={{
            position: 'absolute', left: 8, color: 'var(--text-secondary)', pointerEvents: 'none',
          }} />
          <input
            type="text"
            className="form-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes…"
            style={{ paddingLeft: 26, paddingRight: searchQuery ? 22 : 8, fontSize: '0.8rem', height: 28, width: 200 }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              title="Clear"
              style={{
                position: 'absolute', right: 4, background: 'transparent', border: 'none',
                padding: 2, cursor: 'pointer', color: 'var(--text-secondary)',
              }}
            >
              <IconSystem.x size={12} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setAutoNumberConfirmOpen(true)}
          title="Compact 1..N across included notes by current number, then template order"
          disabled={loading || autoNumbering || rows.length === 0}
        >
          <IconSystem.list size={14} />
          {autoNumbering ? 'Numbering…' : 'Auto-number Notes'}
        </button>
        {/* FSA-2.4 — Actual / Rounded display toggle. Pill style
            matching Note Modal + Statements header. Applies to every
            amount column on the grid (CY/PY Closing). */}
        <span style={displayToggleWrapStyle} role="group" aria-label="Display mode">
          <button
            type="button"
            style={displayToggleBtnStyle(displayMode === 'actual')}
            onClick={() => setDisplayMode('actual')}
            title="Show actual values"
          >
            Actual
          </button>
          <button
            type="button"
            style={displayToggleBtnStyle(displayMode === 'rounded')}
            onClick={() => setDisplayMode('rounded')}
            title="Show rounded values"
          >
            Rounded
          </button>
        </span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={refetch} title="Refresh" disabled={loading}>
          <IconSystem.refresh size={14} />
        </button>
      </div>
      {/* FSA-2.4 — Status filter chip strip. Single row; each chip
          carries its count. Click filters the grid; compose with the
          search input (search applies WITHIN the filtered set). */}
      <div style={filterChipStripStyle}>
        {([
          { key: 'all',       label: 'All',           color: 'neutral' as const },
          { key: 'draft',     label: 'Draft',         color: 'draft' as const },
          { key: 'submitted', label: 'Submitted',     color: 'submitted' as const },
          { key: 'approved',  label: 'Approved',      color: 'approved' as const },
          { key: 'excluded',  label: 'Not Applicable', color: 'neutral' as const },
          { key: 'drift',     label: '⚠ Has drift',   color: 'warning' as const },
        ] as const).map(chipDef => {
          const active = statusFilter === chipDef.key;
          const count = statusFilterCounts[chipDef.key];
          return (
            <button
              key={chipDef.key}
              type="button"
              onClick={() => setStatusFilter(chipDef.key)}
              style={filterChipStyle(active, chipDef.color)}
              aria-pressed={active}
            >
              {chipDef.label}
              <span style={filterChipCountStyle(active)}>{count}</span>
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <SyutGrid
          data={filteredRows as any}
          columns={columns as any}
          enableSorting
          frozenRightColumns={['actions']}
          emptyMessage={loading
            ? 'Loading…'
            : searchQuery.trim() || statusFilter !== 'all'
              ? `No notes match the current filter${searchQuery.trim() ? ` "${searchQuery.trim()}"` : ''}.`
              : 'No notes found for this template.'}
        />
      </div>

      <FsaNoteModal
        isOpen={openSchedule != null}
        engagementId={engagementId}
        scheduleId={openSchedule?.scheduleId ?? null}
        scheduleHeader={openSchedule ? { code: openSchedule.scheduleCode, name: openSchedule.scheduleName } : undefined}
        onClose={() => setOpenSchedule(null)}
        onSaved={refetch}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        minimizeRef={noteModalMinimizeRef}
      />

      {/* Pre-confirm Mark Excluded (checks server-side for saved data → 409
          drops us into the second ConfirmDialog). */}
      <ConfirmDialog
        open={pendingExclude != null}
        title="Mark Note Excluded"
        message={
          pendingExclude
            ? `Skip "${pendingExclude.scheduleName}" from the consolidated FS PDF? Cross-references on BS / P&L will render "—" for this note.`
            : ''
        }
        confirmLabel="Mark Excluded"
        cancelLabel="Cancel"
        variant="danger"
        loading={processingExclusionFor === pendingExclude?.scheduleId}
        loadingMessage="Excluding…"
        onConfirm={() => pendingExclude && performExclude(pendingExclude, false)}
        onCancel={() => setPendingExclude(null)}
      />

      {/* Second-step confirm: clear saved data first. */}
      <ConfirmDialog
        open={confirmClearFor != null}
        title="Clear Saved Data First?"
        message={
          confirmClearFor
            ? `"${confirmClearFor.scheduleName}" has saved content. Excluding will reset the workspace data and totals. Continue?`
            : ''
        }
        confirmLabel="Clear & Exclude"
        cancelLabel="Keep Data"
        variant="danger"
        loading={processingExclusionFor === confirmClearFor?.scheduleId}
        loadingMessage="Clearing…"
        onConfirm={() => confirmClearFor && performExclude(confirmClearFor, true)}
        onCancel={() => setConfirmClearFor(null)}
      />

      {/* Auto-number bulk confirm. */}
      <ConfirmDialog
        open={autoNumberConfirmOpen}
        title="Auto-number Notes"
        message="Re-number all included notes 1..N. Existing numbers are kept where possible; gaps get filled in template order. Excluded notes stay un-numbered. Continue?"
        confirmLabel="Auto-number"
        cancelLabel="Cancel"
        variant="default"
        loading={autoNumbering}
        loadingMessage="Numbering…"
        onConfirm={handleAutoNumber}
        onCancel={() => setAutoNumberConfirmOpen(false)}
      />

      {/* FSA-2.3 — move-to-position confirm. */}
      <ConfirmDialog
        open={pendingMove != null}
        title="Move Note to Position"
        message={
          pendingMove
            ? `Move "${pendingMove.row.scheduleName}" to position ${pendingMove.targetPosition}? ` +
              `Other included notes will be renumbered to fill the gap and make room — ` +
              `like inserting a row in a spreadsheet. ` +
              (pendingMove.targetPosition > (rows ?? []).filter(r => !r.isExcluded).length
                ? `The position you entered is past the last note; it will land at the end.`
                : '')
            : ''
        }
        confirmLabel="Move"
        cancelLabel="Cancel"
        variant="default"
        loading={numberSavingFor === pendingMove?.row.scheduleId}
        loadingMessage="Reordering…"
        onConfirm={confirmMove}
        onCancel={() => setPendingMove(null)}
      />
    </div>
  );
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};

const chip = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.7rem', fontWeight: 600,
});

// (drift chip moved to RowBadges.tsx — see LedgerDriftChip.)

// FSA-2.4 — Display-mode pill toggle (matches Note Modal + Statements
// header pattern). Persists per-screen in localStorage.
const displayToggleWrapStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'stretch',
  border: '1px solid var(--border-color)',
  borderRadius: 999,
  overflow: 'hidden',
  background: 'var(--bg-secondary)',
};
const displayToggleBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '2px 10px',
  fontSize: '0.7rem',
  fontWeight: 600,
  background: active ? '#2563eb' : 'transparent',
  color: active ? '#ffffff' : 'var(--text-secondary)',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
});

// FSA-2.4 — Status filter chip strip (single row, count badges).
// Composes with the search input — filters apply on top of each other.
const filterChipStripStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
  overflowX: 'auto',
  flexShrink: 0,
};
type ChipColor = 'neutral' | 'draft' | 'submitted' | 'approved' | 'warning';
const filterChipStyle = (active: boolean, color: ChipColor): React.CSSProperties => {
  const palettes: Record<ChipColor, { bg: string; fg: string; activeBg: string; activeFg: string }> = {
    neutral:   { bg: 'transparent', fg: 'var(--text-secondary)', activeBg: '#2563eb',  activeFg: '#ffffff' },
    draft:     { bg: 'transparent', fg: '#0369a1',               activeBg: '#0284c7',  activeFg: '#ffffff' },
    submitted: { bg: 'transparent', fg: '#1e40af',               activeBg: '#2563eb',  activeFg: '#ffffff' },
    approved:  { bg: 'transparent', fg: '#166534',               activeBg: '#16a34a',  activeFg: '#ffffff' },
    warning:   { bg: 'transparent', fg: '#92400e',               activeBg: '#d97706',  activeFg: '#ffffff' },
  };
  const p = palettes[color];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: `1px solid ${active ? p.activeBg : 'var(--border-color)'}`,
    background: active ? p.activeBg : p.bg,
    color: active ? p.activeFg : p.fg,
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };
};
const filterChipCountStyle = (active: boolean): React.CSSProperties => ({
  fontSize: '0.65rem',
  fontWeight: 700,
  padding: '1px 6px',
  borderRadius: 999,
  background: active ? 'rgba(255,255,255,0.25)' : 'var(--border-color)',
  color: active ? '#ffffff' : 'var(--text-secondary)',
  minWidth: 18,
  textAlign: 'center',
});

export default FsaNotesTab;
